/**
 * CSV, for the **Export CSV** buttons the approved mockups put on tables.
 *
 * Split into a pure `toCsv` and an impure `downloadCsv` on purpose: the
 * quoting rules are the part that can be wrong in a way nothing notices —
 * a keyword containing a comma silently becomes two columns — and they are
 * only testable if they are not tangled up with `Blob` and `createObjectURL`,
 * which jsdom does not implement the same way a browser does.
 *
 * Three decisions worth stating, because each one is a bug somewhere else:
 *
 * **Numbers are written raw, never through `toLocaleString('en-IN')`.** Every
 * table in this app groups its digits — `2,400` — and that is right on screen
 * and catastrophic in a CSV, where the comma is the delimiter. The screen
 * formats; the export does not.
 *
 * **The file is written with a BOM.** Excel on Windows reads a UTF-8 CSV as
 * the local code page unless one is present, which turns a Telugu or Hindi
 * keyword into mojibake — and this product sells to Hyderabad businesses whose
 * keywords are exactly that.
 *
 * **A cell that Excel would execute is defused.** Keywords come from tenants
 * and from a third-party vendor, so a value beginning `=`, `+`, `@` or a
 * control character is prefixed with an apostrophe: it displays as itself and
 * is not run. A leading `-` is deliberately left alone — it is far more often
 * a real character than a formula, and Excel renders `-kitchen` as text.
 */

/** What a table cell can hold. `null`/`undefined` are written as an empty field. */
export type CsvCell = string | number | null | undefined

/** The leading characters Excel and Sheets treat as the start of a formula. */
const FORMULA = /^[=+@\t\r]/

/** Characters that force RFC 4180 quoting. */
const NEEDS_QUOTES = /["\r\n,]/

function field(value: CsvCell): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : ''
  const defused = FORMULA.test(value) ? `'${value}` : value
  return NEEDS_QUOTES.test(defused) ? `"${defused.replace(/"/g, '""')}"` : defused
}

/**
 * A header row and its rows as RFC 4180 text, CRLF-separated.
 *
 * No trailing newline: a trailing CRLF is read as an empty final record by
 * some parsers, and every row here is meant to be a keyword.
 */
export function toCsv(header: readonly string[], rows: readonly (readonly CsvCell[])[]): string {
  return [header, ...rows].map((row) => row.map(field).join(',')).join('\r\n')
}

/**
 * Hands the file to the browser.
 *
 * The object URL is revoked immediately after the click rather than left for
 * the page's lifetime — the anchor has already been followed by then, and an
 * unrevoked blob holds the whole export in memory until the tab closes.
 */
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
